import React from 'react';
import { motion } from 'framer-motion';

interface RoyalGoldSmokeTextProps {
  children: React.ReactNode;
  as?: 'h1' | 'h2' | 'h3' | 'p' | 'span' | 'div';
  className?: string;
  style?: React.CSSProperties;
  delay?: number;
  duration?: number;
  glowColor?: string;
  splitWords?: boolean;
}

/**
 * RoyalGoldSmokeText — Mewar Royal Gold-Smoke Incense & Stencil Blur Engine.
 *
 * Simulates letters condensing out of sacred camphor yajna smoke & liquid gold dust,
 * resolving into imperial 24-Karat gold leaf calligraphy with a traveling foil sheen.
 */
export function RoyalGoldSmokeText({
  children,
  as = 'div',
  className = '',
  style = {},
  delay = 0.1,
  duration = 1.05,
  glowColor = 'rgba(212, 175, 55, 0.65)',
  splitWords = false,
}: RoyalGoldSmokeTextProps) {
  const Component = motion[as] as any;

  if (splitWords && typeof children === 'string') {
    const words = children.split(' ');
    return (
      <Component className={`inline-flex flex-wrap gap-x-2.5 ${className}`} style={style}>
        {words.map((word, index) => (
          <motion.span
            key={`${word}-${index}`}
            initial={{
              filter: 'blur(24px) drop-shadow(0 0 35px #D4AF37)',
              opacity: 0,
              scale: 0.94,
              letterSpacing: '0.25em',
            }}
            whileInView={{
              filter: 'blur(0px) drop-shadow(0 2px 10px rgba(0,0,0,0.95))',
              opacity: 1,
              scale: 1.0,
              letterSpacing: 'normal',
            }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{
              duration,
              delay: delay + index * 0.09,
              ease: [0.16, 1, 0.3, 1],
            }}
            className="inline-block"
            style={{
              textShadow: `0 2px 16px rgba(0,0,0,0.95), 0 0 28px ${glowColor}`,
            }}
          >
            {word}
          </motion.span>
        ))}
      </Component>
    );
  }

  return (
    <Component
      initial={{
        filter: 'blur(26px) drop-shadow(0 0 40px #D4AF37)',
        opacity: 0,
        scale: 0.95,
        letterSpacing: '0.3em',
      }}
      whileInView={{
        filter: 'blur(0px) drop-shadow(0 3px 14px rgba(0,0,0,0.98))',
        opacity: 1,
        scale: 1.0,
        letterSpacing: 'normal',
      }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{
        duration,
        delay,
        ease: [0.16, 1, 0.3, 1],
      }}
      className={`${className} royal-gold-shimmer-active`}
      style={{
        textShadow: `0 3px 22px rgba(0,0,0,0.98), 0 0 35px ${glowColor}`,
        transform: 'translateZ(0)',
        willChange: 'filter, transform',
        backfaceVisibility: 'hidden',
        ...style,
      }}
    >
      {children}
    </Component>
  );
}

/**
 * RoyalGoldCrestBadge — An imperial Mewar 24K gold foil crest that unblurs and rotates into place.
 */
export function RoyalGoldCrestBadge({
  children,
  className = '',
  delay = 0.05,
  icon = '⚜️',
}: {
  children: React.ReactNode;
  className?: string;
  delay?: number;
  icon?: string;
}) {
  return (
    <motion.div
      initial={{ filter: 'blur(20px) drop-shadow(0 0 30px #D4AF37)', opacity: 0, scale: 0.86, rotate: -6 }}
      whileInView={{ filter: 'blur(0px) drop-shadow(0 0 16px rgba(212,175,55,0.4))', opacity: 1, scale: 1, rotate: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.85, delay, ease: [0.16, 1, 0.3, 1] }}
      className={`inline-flex items-center gap-2.5 px-5 py-1.5 rounded-full border border-amber-400/60 bg-amber-950/70 backdrop-blur-xl shadow-[0_0_28px_rgba(212,175,55,0.45)] text-amber-200 text-xs font-serif tracking-[0.25em] uppercase ${className}`}
    >
      <span className="text-sm">{icon}</span>
      <span>{children}</span>
    </motion.div>
  );
}
